import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Package, AlertTriangle, DollarSign, Plus, Search, Edit2, Trash2,
  History, ArrowDownUp, RefreshCw, CheckCircle2, XCircle, ListChecks, Tag,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catalog of predefined add-on IDs the user can link an inventory item to.
// Source of truth in /app/frontend/src/constants/bookingCatalog.js — this is a
// curated subset used by the picker. Free-form input is also allowed.
const KNOWN_ADDON_IDS = [
  { id: 'print_cap',         label: 'Print a Cap' },
  { id: 'print_tshirt',      label: 'Print a T-Shirt' },
  { id: 'print_mug',         label: 'Print a Mug' },
  { id: 'byo_print',         label: 'Bring Your Own item to Print' },
  { id: 'diy_cake_custom',   label: 'DIY Custom Cake' },
  { id: 'extra_pizza',       label: 'Extra Pizza' },
  { id: 'goodie_bag',        label: 'Goodie Bag' },
  { id: 'gf_df_cupcake',     label: 'GF/DF Cupcake' },
  { id: 'helium_balloons',   label: 'Helium Balloons' },
  { id: 'cup_cakes',         label: 'Cup Cakes' },
  { id: 'cheese_cake',       label: 'Cheese Cake' },
  { id: 'brownies',          label: 'Brownies' },
  { id: 'birthday_candles',  label: 'Birthday Candles' },
  { id: 'bobie_bear',        label: 'Bobie the Bear' },
  { id: 'plushies',          label: 'Plushies' },
  { id: 'chocolate_pops',    label: 'Chocolate Pops' },
];
const KNOWN_LABEL = (id) => KNOWN_ADDON_IDS.find((a) => a.id === id)?.label || id;

const CAT_ACCENT = {
  'Arts & Crafts':       '#FF7A2E',
  'Printing Supplies':   '#6366F1',
  'Edible Supplies':     '#06D6A0',
  'Decor & Party Setup': '#FFD166',
  'Other':               '#9a9aa6',
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty   = (n) => Number(n || 0).toLocaleString('en-CA', { maximumFractionDigits: 3 });

export const CrmInventory = () => {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [category, setCategory] = useState('all');
  const [lowOnly, setLowOnly] = useState(false);
  const [editing, setEditing] = useState(null);   // null | 'new' | item
  const [adjusting, setAdjusting] = useState(null); // item being adjusted
  const [historyFor, setHistoryFor] = useState(null); // item

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (debounced) params.search = debounced;
      if (lowOnly) params.low_stock_only = true;
      const [sumRes, itemsRes, pendRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/inventory/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/inventory/items`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/inventory/pending-deductions`, { withCredentials: true }),
      ]);
      setSummary(sumRes.data);
      setItems(itemsRes.data?.items || []);
      setPending(pendRes.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load inventory');
    } finally {
      setLoading(false);
    }
  }, [category, debounced, lowOnly]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const categories = useMemo(() => summary?.categories || [], [summary]);

  // Total potential revenue = Σ (current_stock × selling_price) across all items
  // shown on the page. Mirrors the backend's total_value computation.
  const potentialRevenue = useMemo(
    () => items.reduce(
      (s, it) => s + (Number(it.current_stock) || 0) * (Number(it.selling_price) || 0),
      0,
    ),
    [items],
  );

  return (
    <div className="space-y-5 max-w-7xl" data-testid="crm-inventory">
      <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Inventory</h1>
        <p className="text-[#6B6B7A] text-sm mt-1">
          Track raw materials and finished goods · auto-deduct when bookings are marked complete.
        </p>
      </motion.div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SummaryCard
          icon={Package}
          label="Total items"
          value={summary?.total_items ?? '—'}
          color="#6366F1"
          testId="inv-card-total"
        />
        <SummaryCard
          icon={AlertTriangle}
          label="Low stock"
          value={summary?.low_stock_count ?? '—'}
          color={summary?.low_stock_count ? '#FF3B6B' : '#06D6A0'}
          highlight={Boolean(summary?.low_stock_count)}
          onClick={() => setLowOnly(true)}
          testId="inv-card-low-stock"
        />
        <SummaryCard
          icon={DollarSign}
          label="Inventory value"
          value={fmtMoney(summary?.total_value || 0)}
          color="#06D6A0"
          sub="(at latest cost)"
          testId="inv-card-value"
        />
        <SummaryCard
          icon={TrendingUp}
          label="Potential revenue"
          value={fmtMoney(potentialRevenue)}
          color="#9333EA"
          sub="(if all stock sold)"
          testId="inv-card-potential"
        />
        <SummaryCard
          icon={ListChecks}
          label="Pending deductions"
          value={summary?.pending_deductions ?? 0}
          color={summary?.pending_deductions ? '#FF7A2E' : '#9a9aa6'}
          highlight={Boolean(summary?.pending_deductions)}
          testId="inv-card-pending"
        />
      </div>

      {/* Pending deductions panel — only visible when there are any */}
      {pending.length > 0 && (
        <PendingDeductionsPanel
          pending={pending}
          onChanged={fetchAll}
        />
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-3 flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#6B6B7A] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, SKU, notes…"
              className="h-8 pl-8 rounded-xl border-[#E8E5DF] text-xs w-64"
              data-testid="inv-search"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-8 rounded-xl border border-[#E8E5DF] text-xs bg-white px-3"
            data-testid="inv-category-filter"
          >
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            type="button"
            onClick={() => setLowOnly((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              lowOnly ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
            }`}
            data-testid="inv-toggle-low-only"
          >
            {lowOnly ? 'Showing low stock only' : 'Low stock only'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => fetchAll()}
            disabled={loading}
            size="sm"
            variant="outline"
            className="rounded-full text-xs border-[#E8E5DF]"
            data-testid="inv-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setEditing('new')}
            size="sm"
            className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="inv-add-item"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Items table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>
        ) : items.length === 0 ? (
          <EmptyState onAdd={() => setEditing('new')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inv-items-table">
              <thead className="bg-[#FFFDF8] text-[11px] uppercase tracking-wider text-[#6B6B7A]">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Item</th>
                  <th className="text-left px-4 py-2 font-semibold">Category</th>
                  <th className="text-right px-4 py-2 font-semibold">Stock</th>
                  <th className="text-right px-4 py-2 font-semibold">Cost</th>
                  <th className="text-right px-4 py-2 font-semibold">Selling</th>
                  <th className="text-right px-4 py-2 font-semibold">Margin</th>
                  <th className="text-left px-4 py-2 font-semibold">Linked add-ons</th>
                  <th className="text-right px-4 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EDE6]">
                {items.map((it) => (
                  <ItemRow
                    key={it.item_id}
                    item={it}
                    onAdjust={() => setAdjusting(it)}
                    onHistory={() => setHistoryFor(it)}
                    onEdit={() => setEditing(it)}
                    onDelete={async () => {
                      if (!window.confirm(`Delete ${it.name}? This soft-deletes the item — its transaction history is preserved.`)) return;
                      try {
                        await axios.delete(`${API_URL}/api/admin/inventory/items/${it.item_id}`, { withCredentials: true });
                        toast.success('Item deleted');
                        fetchAll();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Delete failed');
                      }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {editing && (
        <ItemEditDialog
          mode={editing === 'new' ? 'new' : 'edit'}
          initial={editing === 'new' ? null : editing}
          categories={categories.length ? categories : ['Arts & Crafts','Printing Supplies','Edible Supplies','Decor & Party Setup','Other']}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}
      {adjusting && (
        <StockAdjustDialog
          item={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={() => { setAdjusting(null); fetchAll(); }}
        />
      )}
      {historyFor && (
        <TransactionHistoryDialog
          item={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
};

// ---------- Helpers ----------

const SummaryCard = ({ icon: Icon, label, value, color, sub, highlight, onClick, testId }) => (
  <button
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    className={`text-left bg-white rounded-2xl border-2 p-4 transition-shadow ${onClick ? 'hover:shadow-md cursor-pointer' : 'cursor-default'} ${highlight ? 'animate-pulse-once' : ''}`}
    style={{ borderColor: `${color}33` }}
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{value}</p>
        <p className="text-xs text-[#6B6B7A]">{label}</p>
        {sub && <p className="text-[10px] text-[#9a9aa6]">{sub}</p>}
      </div>
    </div>
  </button>
);

const EmptyState = ({ onAdd }) => (
  <div className="py-16 flex flex-col items-center text-center">
    <Package className="w-12 h-12 text-[#E8E5DF] mb-3" />
    <p className="text-sm font-semibold text-[#1E1E24]">No inventory items yet</p>
    <p className="text-xs text-[#6B6B7A] mt-1 max-w-sm">
      Add your first item — beads, glue, blank caps, mugs, fruit — and start tracking stock.
    </p>
    <Button
      onClick={onAdd}
      size="sm"
      className="rounded-full mt-4 bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
      data-testid="inv-empty-add"
    >
      <Plus className="w-3.5 h-3.5 mr-1" /> Add your first item
    </Button>
  </div>
);

const ItemRow = ({ item, onAdjust, onHistory, onEdit, onDelete }) => {
  const accent = CAT_ACCENT[item.category] || '#9a9aa6';
  const lowStock = item.low_stock;
  return (
    <tr className={lowStock ? 'bg-[#FFF1F4]/60' : ''} data-testid={`inv-row-${item.item_id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-8 rounded-full" style={{ background: accent }} />
          <div className="min-w-0">
            <p className="font-semibold text-[#1E1E24] flex items-center gap-2">
              {item.name}
              {lowStock && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold text-[#FF3B6B] bg-[#FF3B6B]/10 px-2 py-0.5 rounded-full"
                  data-testid={`inv-row-low-${item.item_id}`}
                  title={`Reorder level: ${fmtQty(item.reorder_level)} ${item.unit}`}
                >
                  <AlertTriangle className="w-3 h-3" /> Low
                </span>
              )}
            </p>
            {item.sku && <p className="text-[11px] text-[#9a9aa6]">SKU: {item.sku}</p>}
            {item.notes && <p className="text-xs text-[#6B6B7A] truncate max-w-sm">{item.notes}</p>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" style={{ color: accent, borderColor: `${accent}55` }} className="text-[10px]">
          {item.category}
        </Badge>
      </td>
      <td className="px-4 py-3 text-right">
        <span className={`font-bold ${lowStock ? 'text-[#FF3B6B]' : 'text-[#1E1E24]'}`} data-testid={`inv-row-stock-${item.item_id}`}>
          {fmtQty(item.current_stock)}
        </span>
        <span className="text-xs text-[#6B6B7A] ml-1">{item.unit}</span>
        {item.reorder_level > 0 && (
          <p className="text-[10px] text-[#9a9aa6] mt-0.5">reorder ≤ {fmtQty(item.reorder_level)}</p>
        )}
      </td>
      <td className="px-4 py-3 text-right text-sm">{fmtMoney(item.cost_price)}</td>
      <td className="px-4 py-3 text-right text-sm">{item.selling_price > 0 ? fmtMoney(item.selling_price) : <span className="text-[#9a9aa6]">—</span>}</td>
      <td className="px-4 py-3 text-right text-sm">
        <MarginCell item={item} />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {(item.linked_addon_ids || []).map((aid) => (
            <span key={aid} className="inline-flex items-center gap-1 text-[10px] bg-[#FFFDF8] border border-[#E8E5DF] rounded-full px-2 py-0.5 text-[#6B6B7A]" title={KNOWN_LABEL(aid)}>
              <Tag className="w-2.5 h-2.5" /> {KNOWN_LABEL(aid)}
            </span>
          ))}
          {!(item.linked_addon_ids || []).length && <span className="text-[10px] text-[#9a9aa6]">—</span>}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-1">
          <IconButton title="Adjust stock" onClick={onAdjust} testId={`inv-adjust-${item.item_id}`}>
            <ArrowDownUp className="w-4 h-4" />
          </IconButton>
          <IconButton title="History" onClick={onHistory} testId={`inv-history-${item.item_id}`}>
            <History className="w-4 h-4" />
          </IconButton>
          <IconButton title="Edit" onClick={onEdit} testId={`inv-edit-${item.item_id}`}>
            <Edit2 className="w-4 h-4" />
          </IconButton>
          <IconButton title="Delete" onClick={onDelete} variant="danger" testId={`inv-delete-${item.item_id}`}>
            <Trash2 className="w-4 h-4" />
          </IconButton>
        </div>
      </td>
    </tr>
  );
};

const IconButton = ({ children, title, onClick, variant, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    data-testid={testId}
    className={`p-1.5 rounded-full transition ${
      variant === 'danger'
        ? 'text-[#6B6B7A] hover:bg-[#FF3B6B]/10 hover:text-[#FF3B6B]'
        : 'text-[#6B6B7A] hover:bg-[#FFFDF8] hover:text-[#1E1E24]'
    }`}
  >
    {children}
  </button>
);

const MarginCell = ({ item }) => {
  const cost = Number(item.cost_price) || 0;
  const sell = Number(item.selling_price) || 0;
  if (sell <= 0) return <span className="text-[#9a9aa6]">—</span>;
  const dollars = sell - cost;
  const pct = sell > 0 ? Math.round((dollars / sell) * 100) : 0;
  const color = dollars > 0 ? '#06D6A0' : dollars < 0 ? '#FF3B6B' : '#9a9aa6';
  return (
    <span
      className="inline-flex flex-col items-end leading-tight"
      title={`Per-unit profit: ${fmtMoney(dollars)} on ${fmtMoney(sell)} (${pct}% margin)`}
      data-testid={`inv-row-margin-${item.item_id}`}
    >
      <span className="font-semibold" style={{ color }}>{fmtMoney(dollars)}</span>
      <span className="text-[10px]" style={{ color }}>{pct}%</span>
    </span>
  );
};

// ---------- Pending deductions panel ----------

const PendingDeductionsPanel = ({ pending, onChanged }) => {
  const [working, setWorking] = useState(false);
  const groups = useMemo(() => {
    const map = new Map();
    pending.forEach((p) => {
      const key = p.booking_id;
      if (!map.has(key)) {
        map.set(key, {
          booking_id: p.booking_id,
          customer_name: p.customer_name,
          child_name: p.child_name,
          party_date: p.party_date,
          rows: [],
        });
      }
      map.get(key).rows.push(p);
    });
    return Array.from(map.values());
  }, [pending]);

  const handleApply = async (ids) => {
    setWorking(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/inventory/pending-deductions/apply-bulk`,
        { pending_ids: ids }, { withCredentials: true });
      toast.success(`Applied ${res.data?.applied} of ${res.data?.total} deductions`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setWorking(false);
    }
  };

  const handleSkip = async (id) => {
    const reason = window.prompt('Why skip this deduction? (optional)');
    if (reason === null) return;
    setWorking(true);
    try {
      await axios.post(`${API_URL}/api/admin/inventory/pending-deductions/${id}/skip`,
        { reason }, { withCredentials: true });
      toast.success('Skipped');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Skip failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="bg-[#FFF8E6] border-2 border-[#FFD166] rounded-2xl p-4"
      data-testid="inv-pending-panel"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#FFD166]/30 flex items-center justify-center flex-shrink-0">
          <ListChecks className="w-5 h-5 text-[#B3770A]" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-[#1E1E24]">Pending inventory deductions</p>
          <p className="text-xs text-[#6B6B7A]">
            Generated when parties are marked complete · Apply to deduct stock, Skip to dismiss without effect.
          </p>
        </div>
        <Button
          onClick={() => handleApply(pending.map((p) => p.pending_id))}
          disabled={working}
          size="sm"
          className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white text-xs whitespace-nowrap"
          data-testid="inv-pending-apply-all"
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Apply all ({pending.length})
        </Button>
      </div>
      <ul className="space-y-2">
        {groups.map((g) => (
          <li key={g.booking_id} className="bg-white rounded-xl border border-[#F0EDE6] p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-semibold text-[#1E1E24]">
                {g.child_name || g.customer_name || 'Booking'} <span className="text-[#9a9aa6] font-normal">· {g.party_date || ''}</span>
              </p>
              <Button
                onClick={() => handleApply(g.rows.map((r) => r.pending_id))}
                disabled={working}
                size="sm"
                variant="outline"
                className="rounded-full text-xs h-7 border-[#06D6A0] text-[#06D6A0] hover:bg-[#06D6A0]/10"
              >
                Apply party
              </Button>
            </div>
            <ul className="divide-y divide-[#F8F6F1] mt-1">
              {g.rows.map((r) => (
                <li key={r.pending_id} className="py-1.5 flex items-center justify-between gap-3 text-sm" data-testid={`inv-pending-row-${r.pending_id}`}>
                  <span className="text-[#1E1E24]">
                    <strong>{fmtQty(r.quantity)} {r.item_unit}</strong> of <strong>{r.item_name}</strong>
                    <span className="text-[#9a9aa6] text-xs ml-1">· {r.addon_label}</span>
                  </span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleApply([r.pending_id])}
                      disabled={working}
                      className="p-1.5 rounded-full text-[#06D6A0] hover:bg-[#06D6A0]/10"
                      title="Apply"
                      data-testid={`inv-pending-apply-${r.pending_id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSkip(r.pending_id)}
                      disabled={working}
                      className="p-1.5 rounded-full text-[#6B6B7A] hover:bg-[#FF3B6B]/10 hover:text-[#FF3B6B]"
                      title="Skip"
                      data-testid={`inv-pending-skip-${r.pending_id}`}
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------- Add / Edit Item ----------

const ItemEditDialog = ({ mode, initial, categories, onClose, onSaved }) => {
  const [name, setName] = useState(initial?.name || '');
  const [category, setCategory] = useState(initial?.category || categories[0] || 'Other');
  const [unit, setUnit] = useState(initial?.unit || 'each');
  const [costPrice, setCostPrice] = useState(initial?.cost_price ?? 0);
  const [sellingPrice, setSellingPrice] = useState(initial?.selling_price ?? 0);
  const [currentStock, setCurrentStock] = useState(initial?.current_stock ?? 0);
  const [reorderLevel, setReorderLevel] = useState(initial?.reorder_level ?? 0);
  const [sku, setSku] = useState(initial?.sku || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [linked, setLinked] = useState(initial?.linked_addon_ids || []);
  const [customAddon, setCustomAddon] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleLinked = (aid) => {
    setLinked((prev) => prev.includes(aid) ? prev.filter((x) => x !== aid) : [...prev, aid]);
  };
  const addCustomLink = () => {
    const v = customAddon.trim();
    if (!v) return;
    if (!linked.includes(v)) setLinked((prev) => [...prev, v]);
    setCustomAddon('');
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!category) { toast.error('Category is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category,
        unit: (unit || 'each').trim(),
        cost_price: Number(costPrice) || 0,
        selling_price: Number(sellingPrice) || 0,
        reorder_level: Number(reorderLevel) || 0,
        sku: sku.trim() || null,
        notes: notes.trim() || null,
        linked_addon_ids: linked,
      };
      if (mode === 'new') {
        payload.current_stock = Number(currentStock) || 0;
        await axios.post(`${API_URL}/api/admin/inventory/items`, payload, { withCredentials: true });
        toast.success('Item added');
      } else {
        await axios.patch(`${API_URL}/api/admin/inventory/items/${initial.item_id}`, payload, { withCredentials: true });
        toast.success('Item updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="inv-edit-dialog">
        <DialogHeader>
          <DialogTitle>{mode === 'new' ? 'Add inventory item' : `Edit ${initial?.name}`}</DialogTitle>
          <DialogDescription>
            {mode === 'new'
              ? 'Add a new item with starting stock. Cost price is the price you pay; selling price is what you charge customers (optional).'
              : 'Update item details. To change stock quantity, use the Adjust button on the row instead — that creates an audit trail.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <Field label="Name" full>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl border-[#E8E5DF]" data-testid="inv-edit-name" />
          </Field>
          <Field label="Category">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF3B6B]/40"
              data-testid="inv-edit-category"
            >
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Unit (each / pack / g / ml…)">
            <Input value={unit} onChange={(e) => setUnit(e.target.value)} className="rounded-xl border-[#E8E5DF]" data-testid="inv-edit-unit" />
          </Field>
          <Field label="Cost price ($/unit)">
            <Input type="number" step="0.01" min="0" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} className="rounded-xl border-[#E8E5DF]" data-testid="inv-edit-cost" />
          </Field>
          <Field label="Selling price ($/unit, optional)">
            <Input type="number" step="0.01" min="0" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} className="rounded-xl border-[#E8E5DF]" />
          </Field>
          {mode === 'new' && (
            <Field label="Starting stock">
              <Input type="number" step="0.001" min="0" value={currentStock} onChange={(e) => setCurrentStock(e.target.value)} className="rounded-xl border-[#E8E5DF]" data-testid="inv-edit-stock" />
            </Field>
          )}
          <Field label="Reorder level (warn when stock ≤)">
            <Input type="number" step="0.001" min="0" value={reorderLevel} onChange={(e) => setReorderLevel(e.target.value)} className="rounded-xl border-[#E8E5DF]" />
          </Field>
          <Field label="SKU (optional)">
            <Input value={sku} onChange={(e) => setSku(e.target.value)} className="rounded-xl border-[#E8E5DF]" />
          </Field>
          <Field label="Notes" full>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-xl border-[#E8E5DF]" />
          </Field>
          <Field label="Linked add-ons (auto-deduct when a booking has these add-ons)" full>
            <div className="flex flex-wrap gap-1.5">
              {KNOWN_ADDON_IDS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggleLinked(a.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                    linked.includes(a.id) ? 'bg-[#FF3B6B] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
                  }`}
                  data-testid={`inv-edit-link-${a.id}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5 mt-2">
              <Input
                value={customAddon}
                onChange={(e) => setCustomAddon(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomLink())}
                placeholder="Custom add-on ID (e.g. fruit_kit)"
                className="rounded-xl border-[#E8E5DF] text-xs h-8"
              />
              <Button onClick={addCustomLink} size="sm" variant="outline" className="rounded-full text-xs h-8">+ Add</Button>
            </div>
            {linked.filter((id) => !KNOWN_ADDON_IDS.find((a) => a.id === id)).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="text-[10px] text-[#6B6B7A] font-semibold">Custom:</span>
                {linked.filter((id) => !KNOWN_ADDON_IDS.find((a) => a.id === id)).map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleLinked(id)}
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#FF3B6B] text-white"
                  >
                    {id} ✕
                  </button>
                ))}
              </div>
            )}
          </Field>
        </div>
        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="inv-edit-save">
            {saving ? 'Saving…' : (mode === 'new' ? 'Add item' : 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, full, children }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="text-xs font-semibold text-[#1E1E24]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

// ---------- Stock adjust ----------

const ADJUST_TYPES = [
  { value: 'purchase',   label: 'Purchase / receive new stock', sign: '+' },
  { value: 'usage',      label: 'Usage (party / sold as add-on)', sign: '−' },
  { value: 'wastage',    label: 'Wastage / damaged / lost',     sign: '−' },
  { value: 'adjustment', label: 'Manual adjustment (set absolute)', sign: '=' },
];

const StockAdjustDialog = ({ item, onClose, onSaved }) => {
  const [type, setType] = useState('purchase');
  const [delta, setDelta] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const meta = ADJUST_TYPES.find((t) => t.value === type);
  const previewStock = useMemo(() => {
    const n = Number(delta);
    if (isNaN(n) || !item) return null;
    if (type === 'purchase') return Number(item.current_stock || 0) + n;
    if (type === 'usage' || type === 'wastage') return Number(item.current_stock || 0) - n;
    if (type === 'adjustment') return n;
    return null;
  }, [delta, type, item]);

  const save = async () => {
    const n = Number(delta);
    if (!isFinite(n) || n < 0) { toast.error('Enter a valid number'); return; }
    if (type !== 'adjustment' && n <= 0) { toast.error('Enter a positive amount'); return; }
    setSaving(true);
    try {
      const payload = { delta: n, type, note: note.trim() || undefined };
      if (type === 'purchase' && unitCost) payload.unit_cost = Number(unitCost);
      await axios.post(
        `${API_URL}/api/admin/inventory/items/${item.item_id}/adjust`,
        payload, { withCredentials: true },
      );
      toast.success('Stock updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Adjustment failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="inv-adjust-dialog">
        <DialogHeader>
          <DialogTitle>Adjust stock — {item.name}</DialogTitle>
          <DialogDescription>
            Current: <strong>{fmtQty(item.current_stock)} {item.unit}</strong>
            {item.reorder_level > 0 && <span className="text-[#9a9aa6] text-xs ml-2">(reorder ≤ {fmtQty(item.reorder_level)})</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-[#1E1E24]">Type</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {ADJUST_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`px-3 py-2 rounded-xl text-xs font-semibold transition text-left ${
                    type === t.value
                      ? 'bg-[#FF3B6B] text-white'
                      : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'
                  }`}
                  data-testid={`inv-adjust-type-${t.value}`}
                >
                  <span className="font-bold mr-1">{t.sign}</span>{t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1E1E24]">
              {type === 'adjustment' ? 'Set stock to' : 'Quantity'}
            </label>
            <Input
              type="number" step="0.001" min="0"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              className="rounded-xl border-[#E8E5DF] mt-1"
              autoFocus
              data-testid="inv-adjust-delta"
            />
            {previewStock !== null && (
              <p className="text-xs text-[#6B6B7A] mt-1">
                New stock will be: <strong className={previewStock < 0 ? 'text-[#FF3B6B]' : 'text-[#1E1E24]'}>{fmtQty(previewStock)} {item.unit}</strong>
                {previewStock < 0 && ' — overdraft warning'}
              </p>
            )}
          </div>
          {type === 'purchase' && (
            <div>
              <label className="text-xs font-semibold text-[#1E1E24]">Unit cost ($) <span className="font-normal text-[#9a9aa6]">— updates item cost price</span></label>
              <Input
                type="number" step="0.01" min="0"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                placeholder={`Current: ${fmtMoney(item.cost_price)}`}
                className="rounded-xl border-[#E8E5DF] mt-1"
                data-testid="inv-adjust-unit-cost"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#1E1E24]">Note (optional)</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Costco run, party clean-up, took inventory…"
              className="rounded-xl border-[#E8E5DF] mt-1"
              data-testid="inv-adjust-note"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="inv-adjust-save">
            {saving ? 'Saving…' : `Record ${meta?.label.split(' ')[0].toLowerCase() || 'adjustment'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Transaction history ----------

const TYPE_LABEL = {
  purchase: { label: 'Purchase', color: '#06D6A0' },
  usage:    { label: 'Usage',    color: '#FF7A2E' },
  wastage:  { label: 'Wastage',  color: '#FF3B6B' },
  adjustment: { label: 'Manual', color: '#6366F1' },
  auto_booking: { label: 'Auto (booking)', color: '#9333EA' },
};

const TransactionHistoryDialog = ({ item, onClose }) => {
  const [txs, setTxs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/admin/inventory/items/${item.item_id}/transactions`, { withCredentials: true })
      .then((r) => { if (!cancelled) setTxs(r.data?.transactions || []); })
      .catch(() => { if (!cancelled) setTxs([]); });
    return () => { cancelled = true; };
  }, [item.item_id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="inv-history-dialog">
        <DialogHeader>
          <DialogTitle>Transaction history — {item.name}</DialogTitle>
          <DialogDescription>
            Current stock: <strong>{fmtQty(item.current_stock)} {item.unit}</strong>
          </DialogDescription>
        </DialogHeader>
        {txs === null ? (
          <div className="py-8 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-[#FF3B6B]" /></div>
        ) : txs.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#6B6B7A]">No transactions yet.</p>
        ) : (
          <ul className="divide-y divide-[#F0EDE6]">
            {txs.map((t) => {
              const meta = TYPE_LABEL[t.type] || { label: t.type, color: '#9a9aa6' };
              const sign = t.new_stock >= t.prev_stock ? '+' : '−';
              return (
                <li key={t.tx_id} className="py-2 flex items-center justify-between gap-2 text-sm">
                  <div>
                    <span className="font-semibold text-[#1E1E24]">
                      <span style={{ color: meta.color }}>{meta.label}</span>
                      <span className="text-[#9a9aa6] mx-1.5">·</span>
                      {sign}{fmtQty(Math.abs(t.new_stock - t.prev_stock))} {item.unit}
                    </span>
                    <p className="text-[11px] text-[#6B6B7A]">
                      {fmtQty(t.prev_stock)} → {fmtQty(t.new_stock)} {item.unit}
                      {t.note && <span> · {t.note}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] text-[#9a9aa6]">{new Date(t.created_at).toLocaleString('en-CA')}</p>
                    <p className="text-[10px] text-[#6B6B7A]">{t.employee_name}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CrmInventory;
